import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Stack, 
  Alert,
  Box,
  Tab,
  Tabs,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import { httpPost } from '../lib/http';

export default function AdminNotificationDialog({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [researching, setResearching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [searchPrompt, setSearchPrompt] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    type: 'RECRUITMENT',
    target: '', 
    imageUrl: '',
    metadata: '{}'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * ASK THE AGENT: Hits /api/notify/ai/research
   * Fills the form once research is complete
   */
  const handleAiResearch = async () => {
    if (!searchPrompt) return;
    setResearching(true);
    setError(null);
    try {
      const response = await httpPost('/api/notify/ai/research', { query: searchPrompt });
      if (!response.ok) throw new Error('AI Agent failed to gather information');
      
      const data = await response.json();
      if (data.research) {
        setFormData({
          title: data.research.title || '',
          body: data.research.body || '',
          type: data.research.type || 'RECRUITMENT',
          target: data.research.target || '',
          imageUrl: data.research.imageUrl || '',
          metadata: JSON.stringify(data.research.metadata || {}, null, 2)
        });
      }
    } catch (err) {
      setError(`Agent Error: ${err.message}`);
    } finally {
      setResearching(false);
    }
  };

  const handleSend = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const endpoint = tab === 0 
        ? `/api/notify/topic/${formData.target}` 
        : `/api/notify/user/${formData.target}`;
      
      let metadata = {};
      try {
        metadata = JSON.parse(formData.metadata);
      } catch (e) {
        throw new Error('Invalid JSON in metadata field');
      }

      const response = await httpPost(endpoint, {
        ...formData,
        metadata
      });

      if (!response.ok) throw new Error('Failed to dispatch notification');
      
      setSuccess(true);
      // Reset form on success but keep target if it was a broadcast
      setFormData(prev => ({ ...prev, title: '', body: '', imageUrl: '', metadata: '{}' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, bgcolor: 'secondary.main', color: 'white' }}>
        🚀 Admin AI Notification Agent
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed grey.400' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Ask the Research Agent"
              placeholder="e.g. Find latest Bihar SSC recruitment details for 2026"
              value={searchPrompt}
              onChange={(e) => setSearchPrompt(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AutoAwesomeIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      onClick={handleAiResearch} 
                      disabled={researching || !searchPrompt}
                      variant="contained"
                      size="small"
                      startIcon={researching ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {researching ? 'Agent Working...' : 'Research & Fill'}
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth">
            <Tab label="Broadcast to Topic" />
            <Tab label="Direct to User" />
          </Tabs>

          {success && <Alert severity="success">Notification dispatched successfully!</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            fullWidth
            label={tab === 0 ? "Topic Name" : "User ID"}
            name="target"
            value={formData.target}
            onChange={handleChange}
            required
            helperText="Agent will suggest this based on the recruitment state."
          />

          <TextField
            fullWidth
            label="Notification Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Message Body"
            name="body"
            value={formData.body}
            onChange={handleChange}
            required
          />

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select name="type" value={formData.type} label="Type" onChange={handleChange}>
                <MenuItem value="RECRUITMENT">Recruitment Alert</MenuItem>
                <MenuItem value="SYSTEM">System/Maintenance</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
            />
          </Stack>

          {/* New detailed fields inferred from research metadata */}
          <Box sx={{ p: 2, bgcolor: 'secondary.soft', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: 'secondary.main' }}>
               EXTRACTED JOB DETAILS (READ-ONLY PREVIEW)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {JSON.parse(formData.metadata).department && (
                <Box sx={{ bgcolor: 'white', px: 1, py: 0.5, borderRadius: 1, border: '1px solid #ccc', fontSize: '0.75rem', mb: 1 }}>
                  🏢 {JSON.parse(formData.metadata).department}
                </Box>
              )}
              {JSON.parse(formData.metadata).category && (
                <Box sx={{ bgcolor: 'white', px: 1, py: 0.5, borderRadius: 1, border: '1px solid #ccc', fontSize: '0.75rem', mb: 1 }}>
                  🏷️ {JSON.parse(formData.metadata).category}
                </Box>
              )}
              {JSON.parse(formData.metadata).deadline && (
                <Box sx={{ bgcolor: 'white', px: 1, py: 0.5, borderRadius: 1, border: '1px solid #ccc', fontSize: '0.75rem', mb: 1 }}>
                  ⏳ Deadline: {JSON.parse(formData.metadata).deadline}
                </Box>
              )}
            </Stack>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Metadata JSON (Full Agent Payload)"
            name="metadata"
            value={formData.metadata}
            onChange={handleChange}
            sx={{ 
                fontFamily: 'monospace',
                '& .MuiInputBase-input': { fontSize: '0.8rem' }
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} disabled={loading}>Close</Button>
        <Button 
          variant="contained" 
          color="secondary"
          onClick={handleSend} 
          startIcon={<SendIcon />}
          disabled={loading || !formData.target || !formData.title || researching}
        >
          {loading ? 'Sending...' : 'Dispatch Broadcast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
